import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    CircularProgress,
    Alert,
    Button,
    Grid,
    Divider,
    Stack,
    Avatar
} from '@mui/material';
import { ArrowLeft, Mail, Phone, MessageSquare, User, MapPin, Tag } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../../axiosInstance/axiosInstance';
import PermissionWrapper from '../../Components/Auth/PermissionWrapper';

const RetreatEnquiryDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [enquiry, setEnquiry] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchEnquiryDetail = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await axios.get(`/retreat-enquiry/admin/enquiry/${id}`);
            if (response && response.data) {
                const details = response.data.data || response.data || null;
                setEnquiry(details);
            } else {
                setError("Failed to fetch enquiry details.");
            }
        } catch (err) {
            console.error("Fetch Enquiry Detail Error:", err);
            setError(err.response?.data?.message || err.message || "Failed to load enquiry details.");
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        if (id) {
            fetchEnquiryDetail();
        }
    }, [id, fetchEnquiryDetail]);

    const formatDate = (dateString, includeTime = false) => {
        if (!dateString) return 'N/A';
        const options = {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        };
        if (includeTime) {
            options.hour = '2-digit';
            options.minute = '2-digit';
        }
        return new Date(dateString).toLocaleDateString('en-US', options);
    };

    const guest = enquiry?.guest || {};
    const retreat = enquiry?.retreat || {};
    const chat = enquiry?.chat || [];

    return (
        <PermissionWrapper permission="view">
            <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>
                {/* Back Button */}
                <Button
                    disableElevation disableFocusRipple disableRipple disableTouchRipple
                    variant="outlined"
                    startIcon={<ArrowLeft size={18} />}
                    onClick={() => navigate('/retreat-enquiries')}
                    sx={{
                        mb: 4,
                        borderColor: '#FFBD65',
                        color: '#FFBD65',
                        borderRadius: '8px',
                        textTransform: 'none',

                    }}
                >
                    Back to Enquiries
                </Button>

                {error && <Alert severity="error" sx={{ mb: 3, borderRadius: '8px' }}>{error}</Alert>}

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                        <CircularProgress sx={{ color: '#FFBD65' }} />
                    </Box>
                ) : enquiry ? (
                    <Grid container spacing={4}>
                        {/* Left: Guest Profile Info */}
                        <Grid size={{ xs: 12, md: 4 }}>
                            <Stack spacing={3}>
                                {/* Guest Card */}
                                <Paper sx={{ p: 3, borderRadius: '12px', border: '1px solid #E8E8E8' }} elevation={0}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5 }}>
                                        <Avatar
                                            sx={{
                                                width: 50,
                                                height: 50,
                                                bgcolor: '#FFF8ED',
                                                color: '#FFBD65',
                                                border: '1px solid #FFBD65'
                                            }}
                                        >
                                            <User size={24} />
                                        </Avatar>
                                        <Box>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#222' }}>
                                                {guest.name || 'Anonymous Guest'}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                Guest / Enquirer
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Divider sx={{ mb: 2 }} />
                                    <Stack spacing={2}>
                                        {guest.email && (
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                                <Mail size={16} color="#FFBD65" style={{ marginTop: 3, flexShrink: 0 }} />
                                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                                    <Typography variant="caption" color="textSecondary" display="block">Email Address</Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#444', wordBreak: 'break-word' }}>{guest.email}</Typography>
                                                </Box>
                                            </Box>
                                        )}
                                        {guest.phone && (
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                                <Phone size={16} color="#1FC543" style={{ marginTop: 3, flexShrink: 0 }} />
                                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                                    <Typography variant="caption" color="textSecondary" display="block">Phone / WhatsApp</Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#444', wordBreak: 'break-word' }}>
                                                        {guest.countryCode ? `${guest.countryCode} ` : ''}{guest.phone}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        )}
                                    </Stack>
                                </Paper>

                                {/* Host Card */}
                                {enquiry.host && (
                                    <Paper sx={{ p: 3, borderRadius: '12px', border: '1px solid #E8E8E8' }} elevation={0}>
                                        <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1.5, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                                            Assigned Host
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Avatar sx={{ bgcolor: '#E3F2FD', color: '#1976D2', width: 40, height: 40 }}>
                                                {enquiry.host.charAt(0).toUpperCase()}
                                            </Avatar>
                                            <Typography variant="body1" sx={{ fontWeight: 600, color: '#333', textTransform: 'capitalize' }}>
                                                {enquiry.host}
                                            </Typography>
                                        </Box>
                                    </Paper>
                                )}
                            </Stack>
                        </Grid>

                        {/* Right: Retreat details & Message / Chat History */}
                        <Grid size={{ xs: 12, md: 8 }}>
                            <Stack spacing={4}>
                                {/* Retreat Info Card */}
                                {retreat.id && (
                                    <Paper sx={{ p: 3, borderRadius: '12px', border: '1px solid #E8E8E8', overflow: 'hidden' }} elevation={0}>
                                        <Grid container spacing={3}>
                                            {retreat.image && (
                                                <Grid size={{ xs: 12, sm: 4 }}>
                                                    <Box
                                                        component="img"
                                                        src={retreat.image}
                                                        alt={retreat.title}
                                                        sx={{
                                                            width: '100%',
                                                            height: 140,
                                                            objectFit: 'cover',
                                                            borderRadius: '8px',
                                                            border: '1px solid #F0F0F0'
                                                        }}
                                                    />
                                                </Grid>
                                            )}
                                            <Grid size={{ xs: 12, sm: retreat.image ? 8 : 12 }}>
                                                <Stack spacing={1.5} justifyContent="center" sx={{ height: '100%' }}>
                                                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#222', lineHeight: 1.4 }}>
                                                        {retreat.title}
                                                    </Typography>
                                                    {retreat.location && (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#666', fontSize: '0.85rem' }}>
                                                            <MapPin size={16} color="#FFBD65" />
                                                            {retreat.location}
                                                        </Box>
                                                    )}
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mt: 1 }}>
                                                        {retreat.arrivalDate && (
                                                            <Box>
                                                                <Typography variant="caption" color="textSecondary" display="block">Arrival Date</Typography>
                                                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#444' }}>
                                                                    {formatDate(retreat.arrivalDate)}
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                        {retreat.departureDate && (
                                                            <Box>
                                                                <Typography variant="caption" color="textSecondary" display="block">Departure Date</Typography>
                                                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#444' }}>
                                                                    {formatDate(retreat.departureDate)}
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </Stack>
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                )}

                                {/* Conversations / Messages List */}
                                <Paper sx={{ p: 4, borderRadius: '12px', border: '1px solid #E8E8E8' }} elevation={0}>
                                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <MessageSquare size={20} color="#FFBD65" />
                                        Conversation History
                                    </Typography>

                                    {chat.length > 0 ? (
                                        <Stack spacing={2.5}>
                                            {chat.map((msg, index) => {
                                                const isGuest = msg.senderType === 'guest';
                                                const isOffer = msg.type === 'offer';
                                                return (
                                                    <Box
                                                        key={msg.id || index}
                                                        sx={{
                                                            alignSelf: isGuest ? 'flex-start' : 'flex-end',
                                                            maxWidth: '80%',
                                                            minWidth: '280px'
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                p: 2,
                                                                borderRadius: '12px',
                                                                bgcolor: isGuest ? '#F5F5F5' : '#FFF8ED',
                                                                border: isGuest ? '1px solid #E0E0E0' : '1px solid #FFE7C1'
                                                            }}
                                                        >
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, gap: 2 }}>
                                                                <Typography variant="caption" sx={{ fontWeight: 600, color: isGuest ? '#555' : '#efa134', textTransform: 'capitalize', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                    {isOffer && <Tag size={12} style={{ transform: 'rotate(90deg)', marginRight: '2px' }} />}
                                                                    {msg.sender?.name || (isGuest ? 'Guest' : 'Host')}
                                                                    {isOffer && " (Special Offer)"}
                                                                </Typography>
                                                                <Typography variant="caption" color="textSecondary">
                                                                    {formatDate(msg.createdAt, true)}
                                                                </Typography>
                                                            </Box>
                                                            {isOffer && msg.offerDetails ? (
                                                                <Box sx={{ mt: 1, bgcolor: '#FFFFFF', p: 2, borderRadius: '8px', border: '1px dashed #FFE7C1' }}>
                                                                    <Grid container spacing={2}>
                                                                        <Grid size={{ xs: 6 }}>
                                                                            <Typography variant="caption" color="textSecondary" display="block">Proposed Arrival</Typography>
                                                                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
                                                                                {formatDate(msg.offerDetails.arrivalDate)}
                                                                            </Typography>
                                                                        </Grid>
                                                                        <Grid size={{ xs: 6 }}>
                                                                            <Typography variant="caption" color="textSecondary" display="block">Proposed Departure</Typography>
                                                                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
                                                                                {formatDate(msg.offerDetails.departureDate)}
                                                                            </Typography>
                                                                        </Grid>
                                                                        <Grid size={{ xs: 6 }}>
                                                                            <Typography variant="caption" color="textSecondary" display="block">Price</Typography>
                                                                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#2E7D32' }}>
                                                                                {msg.offerDetails.currency || 'INR'} {msg.offerDetails.price?.toLocaleString()}
                                                                            </Typography>
                                                                        </Grid>
                                                                        <Grid size={{ xs: 6 }}>
                                                                            <Typography variant="caption" color="textSecondary" display="block">Deposit Required</Typography>
                                                                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#444' }}>
                                                                                {msg.offerDetails.currency || 'INR'} {msg.offerDetails.deposit?.toLocaleString()}
                                                                            </Typography>
                                                                        </Grid>
                                                                        {msg.offerDetails.lastDateToRegister && (
                                                                            <Grid size={{ xs: 6 }}>
                                                                                <Typography variant="caption" color="textSecondary" display="block">Last Date to Register</Typography>
                                                                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#C62828' }}>
                                                                                    {formatDate(msg.offerDetails.lastDateToRegister)}
                                                                                </Typography>
                                                                            </Grid>
                                                                        )}
                                                                        {msg.offerDetails.customAccommodationSuggestion !== undefined && msg.offerDetails.customAccommodationSuggestion !== null && (
                                                                            <Grid size={{ xs: 6 }}>
                                                                                <Typography variant="caption" color="textSecondary" display="block">Accommodation Option</Typography>
                                                                                <Typography variant="body2" sx={{ fontWeight: 500, color: '#444' }}>
                                                                                    Option #{msg.offerDetails.customAccommodationSuggestion}
                                                                                </Typography>
                                                                            </Grid>
                                                                        )}
                                                                        {msg.offerDetails.status && (
                                                                            <Grid size={{ xs: 12 }}>
                                                                                <Divider sx={{ my: 1 }} />
                                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                                    <Typography variant="caption" color="textSecondary">Status:</Typography>
                                                                                    <Typography
                                                                                        variant="body2"
                                                                                        sx={{
                                                                                            fontWeight: 700,
                                                                                            textTransform: 'uppercase',
                                                                                            fontSize: '0.75rem',
                                                                                            px: 1,
                                                                                            py: 0.25,
                                                                                            borderRadius: '4px',
                                                                                            display: 'inline-block',
                                                                                            bgcolor: msg.offerDetails.status === 'pending' ? '#FFF3E0' : msg.offerDetails.status === 'accepted' ? '#E8F5E9' : '#FFEBEE',
                                                                                            color: msg.offerDetails.status === 'pending' ? '#E65100' : msg.offerDetails.status === 'accepted' ? '#2E7D32' : '#C62828'
                                                                                        }}
                                                                                    >
                                                                                        {msg.offerDetails.status}
                                                                                    </Typography>
                                                                                </Box>
                                                                            </Grid>
                                                                        )}
                                                                    </Grid>
                                                                </Box>
                                                            ) : (
                                                                <Typography variant="body2" sx={{ color: '#444', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                                                                    {msg.message}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                );
                                            })}
                                        </Stack>
                                    ) : (
                                        <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 4 }}>
                                            No messages found in this conversation.
                                        </Typography>
                                    )}
                                </Paper>
                            </Stack>
                        </Grid>
                    </Grid>
                ) : (
                    <Alert severity="warning">No details found for this retreat enquiry.</Alert>
                )}
            </Box>
        </PermissionWrapper>
    );
};

export default RetreatEnquiryDetail;
